import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, selectedCar, isDeleting }) => {
  const { t } = useTranslation();
  // Store original body styles and scroll position
  const bodyStyles = useRef({});

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);

  if (!isOpen || !selectedCar) return null;

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full max-w-5xl my-8 mb-20 md:max-h-[calc(80vh-2rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-rose-50 dark:bg-rose-900/20 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white truncate">
                  {t('quickActions.deleteTitle')}
                </h2>
                {/* <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('quickActions.deleteSubtitle')}
                </p> */}
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-900/30 mb-4">
              <TrashIcon className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            </div>
            
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              {t('quickActions.deleteConfirmTitle')}
            </h3>
            
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 mb-4">
              <div className="text-center">
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {selectedCar.make} {selectedCar.model}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {selectedCar.plate}
                </p>
              </div>
            </div>
            
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {t('quickActions.deleteWarning')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 rounded-b-xl">
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="w-full sm:w-auto bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('quickActions.deleting')}
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4 mr-2" />
                  {t('quickActions.deleteConfirm')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
